import { defineStore } from "pinia";

/**
 * Auth store — Google OAuth only (G.2 + G.5).
 *
 * Persists the current user object to localStorage under `kff-auth`.
 * G.2 added support for `method: "google"` and a session token
 * (`token: string`) stored alongside the user info. The session
 * token is also mirrored to `kff-session-token` for `apiFetch` to
 * read directly (avoids a Pinia round-trip on every API call).
 *
 * G.5 history: pre-G.5 the store also handled `method: "password"`
 * (set by the legacy Login.vue password form) and `method:
 * "dev-bypass"` (set by the "Bypass dev mode" button). G.5 removes
 * those login paths entirely from the UI; however we keep the
 * pre-G.5 `method` values valid on READ so users with stale
 * localStorage (e.g. from a session that pre-dates G.5) don't see
 * a crash or a "logged out" flash — they just become non-functional
 * and must re-login via Google to do anything useful.
 *
 * Backward compatibility:
 *  - `bootstrap()` still accepts `password` and `dev-bypass` on read
 *    (returns the user object as-is). They become non-functional
 *    because `apiFetch` no longer attaches `X-Admin-Password`, so
 *    any subsequent API call returns 401 → `redirectToLogin()`.
 *  - The pre-G.5 `setUser({ ..., method: "password" })` action still
 *    works for callers that re-set an existing user (no-op-ish), but
 *    new password logins are no longer possible from the UI.
 */

const STORAGE_KEY = "kff-auth";
const SESSION_TOKEN_KEY = "kff-session-token";

export type UserRole = "admin" | "reporter" | "pending";

export interface AuthUser {
  email: string;
  method: "password" | "dev-bypass" | "google";
  loggedInAt: string; // ISO timestamp
  name?: string;
  picture?: string;
  /** RBAC role returned by the Worker after Google login. */
  role?: UserRole;
}

/**
 * Discriminated shape persisted to localStorage. Includes the session
 * token + Google profile fields when `method === "google"`. For other
 * methods the token + name/picture fields are absent.
 */
interface PersistedAuth {
  email: string;
  method: AuthUser["method"];
  loggedInAt: string;
  name?: string;
  picture?: string;
  role?: UserRole;
  /**
   * G.2 — session JWT. Only present for `method === "google"`.
   * Used by `getAuthToken()` to attach `Authorization: Bearer` to
   * every `/api/*` request. Backend (G.4) will verify the token;
   * for now (G.2) the Worker ignores the header and falls back to
   * `X-Admin-Password` / `X-API-Key` for everything.
   */
  token?: string;
}

interface AuthState {
  user: AuthUser | null;
  /**
   * G.2 — Bearer session token. Cached in state (and persisted
   * separately under `kff-session-token` for `apiFetch` to read
   * without a Pinia dependency) so login round-trips stay fast.
   */
  token: string | null;
}

function readFromStorage(): AuthUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedAuth;
    if (
      parsed &&
      typeof parsed.email === "string" &&
      (parsed.method === "password" ||
        parsed.method === "dev-bypass" ||
        parsed.method === "google")
    ) {
      const user: AuthUser = {
        email: parsed.email,
        method: parsed.method,
        loggedInAt: parsed.loggedInAt,
        ...(parsed.name !== undefined ? { name: parsed.name } : {}),
        ...(parsed.picture !== undefined ? { picture: parsed.picture } : {}),
        ...(parsed.role !== undefined ? { role: parsed.role } : {}),
      };
      return user;
    }
    return null;
  } catch {
    return null;
  }
}

function readTokenFromStorage(): string | null {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeToStorage(user: AuthUser | null, token: string | null): void {
  try {
    if (user === null) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(SESSION_TOKEN_KEY);
      return;
    }
    // For non-Google methods we deliberately DO NOT include the token
    // field in the persisted user blob (keeps shape minimal + matches
    // pre-G.2 schema for those methods).
    const blob: PersistedAuth = {
      email: user.email,
      method: user.method,
      loggedInAt: user.loggedInAt,
      ...(user.name !== undefined ? { name: user.name } : {}),
      ...(user.picture !== undefined ? { picture: user.picture } : {}),
      ...(user.role !== undefined ? { role: user.role } : {}),
      ...(user.method === "google" && token !== null
        ? { token }
        : {}),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(blob));
    if (user.method === "google" && token !== null) {
      localStorage.setItem(SESSION_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(SESSION_TOKEN_KEY);
    }
  } catch {
    // localStorage may be unavailable (SSR, private mode) — silently ignore.
  }
}

export const useAuthStore = defineStore("auth", {
  state: (): AuthState => ({
    user: null,
    token: null,
  }),

  getters: {
    isAuthenticated: (state): boolean => state.user !== null,
    email: (state): string | null => state.user?.email ?? null,
    /**
     * G.2 — returns the session JWT for `Authorization: Bearer` headers.
     * Returns `null` for password / dev-bypass logins (which still use
     * `X-Admin-Password` instead).
     */
    authToken: (state): string | null => state.token,
    /** RBAC role of the current user. null when not logged in. */
    role: (state): UserRole | null => state.user?.role ?? null,
    isAdmin: (state): boolean => state.user?.role === "admin",
    isReporter: (state): boolean => state.user?.role === "reporter",
    isPending: (state): boolean => state.user?.role === "pending",
  },

  actions: {
    /**
     * Hydrate auth state from localStorage. Call once at app bootstrap.
     * Reads both the user blob (`kff-auth`) and the session token
     * (`kff-session-token`). If the user blob is missing but the token
     * is present (shouldn't normally happen), the token is discarded.
     */
    bootstrap(): void {
      this.user = readFromStorage();
      // Only restore the session token if the user blob is present AND
      // the method is `google`. For password / dev-bypass logins the
      // token is meaningless and we keep `state.token = null` so the
      // `apiFetch` falls back to `X-Admin-Password`.
      this.token =
        this.user !== null && this.user.method === "google"
          ? readTokenFromStorage()
          : null;
    },

    setUser(user: AuthUser): void {
      this.user = user;
      // Preserve the existing token when re-setting a non-Google user
      // (this is the path used by the legacy password login flow —
      // they had no token, we don't want to clobber a Google token if
      // the user is somehow re-running the password flow, but in
      // practice the password login is mutually exclusive with google).
      const nextToken = user.method === "google" ? this.token : null;
      writeToStorage(user, nextToken);
      if (user.method !== "google") {
        this.token = null;
      }
    },

    clearUser(): void {
      this.user = null;
      this.token = null;
      writeToStorage(null, null);
    },

    /**
     * G.5 — Legacy dev-bypass helper, kept for backward-compat with
     * any code that still calls it (none in the current codebase
     * after G.5's Login.vue rewrite). It mutates state to set a
     * non-functional `dev-bypass` user — the same caveat as the
     * pre-G.5 password login: any subsequent API call returns 401
     * because the Worker no longer honors `X-Admin-Password` by
     * default. Kept as a no-op-ish helper so external callers (or
     * future dev tools) don't crash with "method undefined".
     *
     * New code should call `clearUser()` + `loginWithGoogle()` instead.
     */
    devBypass(email: string = "dev@kff.local"): void {
      this.setUser({
        email,
        method: "dev-bypass",
        loggedInAt: new Date().toISOString(),
      });
    },

    /**
     * G.2 — complete a Google OAuth sign-in by persisting the session
     * token + Google profile alongside the user record.
     *
     * Idempotent: re-calling with the same token/user updates the
     * `loggedInAt` timestamp without losing any fields.
     */
    loginWithGoogle(
      token: string,
      user: { email: string; name: string; picture: string; role?: UserRole },
    ): void {
      this.user = {
        email: user.email,
        method: "google",
        loggedInAt: new Date().toISOString(),
        name: user.name,
        picture: user.picture,
        ...(user.role !== undefined ? { role: user.role } : {}),
      };
      this.token = token;
      writeToStorage(this.user, token);
    },
  },
});
